/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
/** @jsx jsx */
import { Fragment, useState } from 'react'
import { css, jsx } from '@emotion/react'

import { EuiCallOut, EuiSpacer, EuiText } from '@elastic/eui'

import type { BillingSubscriptionLevel } from '@modules/ui-types'
import useBillingDetails from '@modules/billing-lib/billingDetails/useBillingDetails'
import { hasUnexpiredSession } from '@modules/auth/auth'
import useGetFeaturesUsage from '@modules/billing-lib/queries/useGetFeaturesUsage'
import { isTrial } from '@modules/billing-lib/billingDetails/utils'
import { useProfile } from '@modules/profile-lib/hooks'
import { useConfig } from '@modules/cui/ConfigContext'
import useOrganizationId from '@modules/billing-lib/hooks/useOrganizationId'

import { isElasticStaff } from '@/lib/validateEmail'

import SelectSubscriptionModal from '../SelectSubscription'
import ReviewSubscriptionChangesModal from '../SelectSubscription/ReviewSubscriptionChangesModal'

import { shouldShowFeatureUsageBanner } from './utils'
import FeatureUsageContent from './FeatureUsageContent'

import type { Props } from './types'

const FeatureUsageBanner = ({ featureName, overrideUsageLevel }: Props) => {
  const [showChangeSubscriptionModal, setShowChangeSubscriptionModal] = useState<boolean>(false)
  const [showReviewChangesModal, setShowReviewChangesModal] = useState<boolean>(false)
  const [upgradeLevel, setUpgradeLevel] = useState<BillingSubscriptionLevel | undefined>()
  const appName = useConfig('APP_NAME')
  const userHasUnexpiredSession = hasUnexpiredSession()
  const organizationId = useOrganizationId()
  const billingDetails = useBillingDetails()
  const profile = useProfile({ enabled: appName === 'userconsole' })

  const featureUsageQuery = useGetFeaturesUsage(
    {
      organizationId: organizationId!,
    },
    { enabled: Boolean(organizationId) },
  )

  if (!userHasUnexpiredSession || !billingDetails.data || !featureUsageQuery.data) {
    return null
  }

  const shouldShowUsageBanner = shouldShowFeatureUsageBanner({
    featureUsage: featureUsageQuery.data,
    isTrial: isTrial(billingDetails.data),
    isElasticStaff: profile?.email ? isElasticStaff(profile.email) : false,
    overrideUsageLevel,
  })

  if (shouldShowUsageBanner) {
    return (
      <Fragment>
        <EuiCallOut
          announceOnMount={true}
          data-test-id='usage-notice'
          iconType='info'
          size='s'
          css={css({
            button: {
              display: 'contents',
            },
            minBlockSize: 'fit-content',
          })}
          title={
            <EuiText size='s' color='default' css={{ display: 'inline' }}>
              <FeatureUsageContent
                featureName={featureName}
                overrideUsageLevel={overrideUsageLevel}
                onReviewChanges={() => {
                  setShowReviewChangesModal(true)
                }}
                onChangeSubscription={() => {
                  setShowChangeSubscriptionModal(true)
                }}
              />
            </EuiText>
          }
        />
        <EuiSpacer size='xs' />
        {showChangeSubscriptionModal && (
          <SelectSubscriptionModal
            overrideUsageLevel={overrideUsageLevel}
            upgradeLevel={upgradeLevel}
            closeModal={() => {
              setShowChangeSubscriptionModal(false)
              setUpgradeLevel(undefined)
            }}
            onSeeReviewChanges={() => {
              setShowChangeSubscriptionModal(false)
              setShowReviewChangesModal(true)
            }}
          />
        )}

        {showReviewChangesModal && (
          <ReviewSubscriptionChangesModal
            closeModal={() => setShowReviewChangesModal(false)}
            onUpgrade={() => {
              setShowReviewChangesModal(false)
              setShowChangeSubscriptionModal(true)
              setUpgradeLevel(featureUsageQuery.data.usage_level as BillingSubscriptionLevel)
            }}
          />
        )}
      </Fragment>
    )
  }

  return null
}

export default FeatureUsageBanner
